// src/components/EventDetails.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import serverURL from "../config";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faUsers,
  faCheckCircle,
  faEdit,
  faTrash,
  faFileAlt,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import LevelModal from "../components/LevelModal";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AssigneeFeedbackList from "../components/AssigneeFeedbackList";
import AssessmentCategoryCards from "../components/AssessmentCategoryCards";
import LessonContent from "../components/LessonContent";
import { toast } from "react-toastify";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [levels, setLevels] = useState([]);
  const [categories, setCategories] = useState([]); // State for categories
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [isOrdering, setIsOrdering] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(1); // Default category ID
  const [questionCount, setQuestionCount] = useState(10); // Default question count
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonContent, setLessonContent] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [lessonName, setLessonName] = useState('');
  const [lessonCategory, setLessonCategory] = useState('Technical');
  const [lessonDifficulty, setLessonDifficulty] = useState('Medium');
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeType, setAssigneeType] = useState('Online');
  const [hoveredLevelId, setHoveredLevelId] = useState(null); // New state for tracking hovered level
  const [allCategories, setAllCategories] = useState([]); // All available categories from DB
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false); // Category selection modal
  const [selectedLevelForConfig, setSelectedLevelForConfig] = useState(null); // Level being configured
  const [isConfirmLevelModalOpen, setIsConfirmLevelModalOpen] = useState(false); // Confirmation modal
  const [isSubmittingLevel, setIsSubmittingLevel] = useState(false); // Submission loading state
  const [editingLevelId, setEditingLevelId] = useState(null); // For inline editing
  const [editingLevelName, setEditingLevelName] = useState(''); // For inline editing
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track changes

  const fetchEventDetails = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${serverURL}/events/${id}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const eventData = response.data;
      setEvent(eventData);

      // Process and load existing levels
      if (eventData.levels && eventData.levels.length > 0) {
        const formattedLevels = eventData.levels.map(level => {
          // Determine type based on is_online_test flag
          const levelType = level.is_online_test ? 'Assessment' : 'Lesson';

          // Process categories if they exist
          let processedCategories = [];
          if (level.categories && Array.isArray(level.categories)) {
            // Filter out null entries (from LEFT JOIN when no categories)
            processedCategories = level.categories
              .filter(cat => cat.category_id !== null)
              .map(cat => ({
                id: `saved-${level.id}-${cat.category_id}`, // Unique ID for UI
                category_id: cat.category_id,
                category_name: cat.category_name,
                questions_count: cat.question_count,
                duration: '30', // Default value
                difficulty: 'Medium' // Default value
              }));
          }

          return {
            id: level.id,
            level_name: level.level_name,
            level_order: level.level_order,
            type: levelType,
            categories: processedCategories
          };
        });

        setLevels(formattedLevels);

        // Auto-select first Assessment level if exists
        const firstAssessment = formattedLevels.find(level => level.type === 'Assessment');
        if (firstAssessment) {
          setSelectedLevel(firstAssessment);
        }
      }
    } catch (error) {
      console.error("Failed to fetch event details:", error);
      toast.error("Failed to load event details");
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${serverURL}/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Fetched Categories:", response.data);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchAllCategories = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${serverURL}/events/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Fetched All Categories:", response.data);
      setAllCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch all categories:", error);
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchEventDetails();
    fetchCategories();
    fetchAllCategories();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openLevelModal = (level = null, mode = "create") => {
    setModalTitle(
      mode === "create" ? "Add Level" : mode === "edit" ? "Edit Level" : "Delete Level"
    );
    setEditingLevel(level);
    setModalMode(mode);
    setIsLevelModalOpen(true);
  };

  const closeLevelModal = () => {
    setIsLevelModalOpen(false);
    setEditingLevel(null);
  };

  const openConfigureModal = () => {
    setIsConfigureModalOpen(true);
  };

  const closeConfigureModal = () => {
    setIsConfigureModalOpen(false);
    setSelectedCategoryId(null); // Reset to default
    setQuestionCount(10); // Reset to default
  };

  const handleEdit = (id) => {
    const levelToEdit = levels.find((level) => level.id === id);
    openLevelModal(levelToEdit, "edit");
  };

  const handleDelete = async (id) => {
    // Check if it's a saved level (positive ID) or new level (negative ID)
    const isSaved = id > 0;

    if (isSaved) {
      // Show confirmation dialog for saved levels
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this level? This action cannot be undone and will remove all associated data.'
      );

      if (!confirmDelete) {
        return;
      }

      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${serverURL}/events/levels/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Level deleted successfully');
      } catch (error) {
        console.error('Error deleting level:', error);
        toast.error('Failed to delete level from database');
        return; // Don't remove from UI if API call fails
      }
    }

    // Remove from local state
    setLevels(levels.filter((level) => level.id !== id));

    // Clear selection if deleted level was selected
    if (selectedLevel?.id === id) {
      setSelectedLevel(null);
    }
    setHasUnsavedChanges(true); // Mark as changed
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(levels);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setLevels(items);
  };

  const toggleOrdering = () => {
    setIsOrdering(!isOrdering);
  };

  const addLevel = (level) => {
    setLevels([...levels, level]);
  };

  const openCategoryConfigModal = (level) => {
    setSelectedLevelForConfig(level);
    setIsCategoryModalOpen(true);
  };

  const closeCategoryConfigModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedLevelForConfig(null);
  };

  const openConfirmLevelModal = () => {
    setIsConfirmLevelModalOpen(true);
  };

  const closeConfirmLevelModal = () => {
    setIsConfirmLevelModalOpen(false);
  };

  const handleConfirmLevel = async () => {
    if (!event || !levels || levels.length === 0) {
      toast.error("No levels to confirm");
      return;
    }

    // Categories are now optional - no validation needed

    setIsSubmittingLevel(true);
    try {
      const token = localStorage.getItem("token");

      // Transform levels array to match backend format
      const formattedLevels = levels.map((level, index) => {
        const baseLevel = {
          id: level.id,
          level_name: level.level_name,
          level_order: index + 1,
          is_online_test: level.type === 'Assessment' ? true : false
        };

        // Only include categories for Assessment levels
        // Transform categories to match backend API expectations (only category_id and question_count)
        if (level.type === 'Assessment' && level.categories && level.categories.length > 0) {
          baseLevel.categories = level.categories.map(cat => ({
            category_id: cat.category_id,
            question_count: parseInt(cat.questions_count) || 0
          }));
        }

        return baseLevel;
      });

      const response = await axios.post(
        `${serverURL}/events/${event.id}/configure-levels`,
        { levels: formattedLevels },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        toast.success("Event levels confirmed and saved successfully!");
        closeConfirmLevelModal();
        setSelectedLevel(null); // Clear selection to refresh UI
        // Refresh event details to get updated data
        await fetchEventDetails();
        setHasUnsavedChanges(false); // Reset change flag
      } else {
        toast.error(response.data.message || "Failed to save levels");
      }
    } catch (error) {
      console.error("Error confirming levels:", error);
      const errorMsg = error.response?.data?.message || "Failed to save event levels";
      toast.error(errorMsg);
    } finally {
      setIsSubmittingLevel(false);
    }
  };

  const handleLessonSubmit = () => {
    const newLevel = {
      id: -(levels.length + 1), // Negative ID for new level
      level_name: lessonName || `Lesson ${levels.length + 1}`,
      type: 'Lesson',
      category: lessonCategory,
      difficulty: lessonDifficulty,
    };
    addLevel(newLevel);
    closeLessonModal();
  };

  const handleAssigneeSubmit = () => {
    const newLevel = {
      id: -(levels.length + 1), // Negative ID for new level
      level_name: assigneeName || `Feedback ${levels.length + 1}`,
      type: 'Feedback',
      assigneeType: assigneeType,
    };
    addLevel(newLevel);
    closeAssigneeModal();
  };

  const handleConfigureQuestions = async (levelId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${serverURL}/events/${event.id}/level/${levelId}/questions`,
        {
          category_id: selectedCategoryId,
          question_count: questionCount,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Assessment configured:", response.data);
      const newLevel = {
        id: -(levels.length + 1), // Negative ID for new level
        level_name: `Assessment ${levels.length + 1}`,
        type: 'Assessment',
      };
      addLevel(newLevel);
      closeConfigModal();
    } catch (error) {
      console.error("Failed to configure questions:", error);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDropdownSelect = (type) => {
    // Use negative IDs for new levels - backend expects this
    // Generate unique negative IDs by using negative index
    const newId = -(levels.length + 1);

    const newLevel = {
      id: newId,
      level_name: `Level ${levels.length + 1}`,
      type: type // Store the type (Lesson, Assessment, Feedback)
    };
    setLevels([...levels, newLevel]);
    setHasUnsavedChanges(true); // Mark as changed
    setIsDropdownOpen(false);
  };

  const handleLevelClick = (level) => {
    setSelectedLevel(level);
  };

  const handleCategoriesChange = (updatedCategories) => {
    if (!selectedLevel) return;

    // Update the categories in the levels array
    const updatedLevels = levels.map(level => {
      if (level.id === selectedLevel.id) {
        return {
          ...level,
          categories: updatedCategories
        };
      }
      return level;
    });

    setLevels(updatedLevels);

    // Also update the selectedLevel to keep UI in sync
    setSelectedLevel({
      ...selectedLevel,
      categories: updatedCategories
    });

    setHasUnsavedChanges(true); // Mark as changed
  };

  const handleEditLevelName = (level) => {
    setEditingLevelId(level.id);
    setEditingLevelName(level.level_name);
  };

  const handleSaveLevelName = () => {
    if (!editingLevelName.trim()) {
      toast.error('Level name cannot be empty');
      return;
    }

    const updatedLevels = levels.map(level =>
      level.id === editingLevelId
        ? { ...level, level_name: editingLevelName }
        : level
    );
    setLevels(updatedLevels);

    if (selectedLevel?.id === editingLevelId) {
      setSelectedLevel({ ...selectedLevel, level_name: editingLevelName });
    }

    setEditingLevelId(null);
    setEditingLevelName('');
    setHasUnsavedChanges(true); // Mark as changed
    toast.success('Level name updated');
  };

  const handleCancelEditLevelName = () => {
    setEditingLevelId(null);
    setEditingLevelName('');
  };

  //   const openConfigModal = (level) => {
  //     setSelectedLevel(level);
  //     setIsConfigModalOpen(true);
  //   };

  //   const closeConfigModal = () => {
  //     setIsConfigModalOpen(false);
  //     setSelectedLevel(null);
  //     // Reset form fields
  //     setLessonTitle('');
  //     setLessonContent('');
  //     setAssigneeName('');
  //     setAssigneeEmail('');
  //     setQuestionCount(10);
  //   };
  const renderLevels = () => (
    <div className="space-y-3">
      {levels.map((level) => (
        <div
          key={level.id}
          className={`bg-white border border-gray-200 p-4 rounded-lg 
                    shadow-md hover:bg-gray-200 transition-shadow duration-200 cursor-pointer relative
                    ${selectedLevel?.id === level.id ? 'bg-gray-200' : ''}`}
          onMouseEnter={() => setHoveredLevelId(level.id)} // Set hovered level ID on mouse enter
          onMouseLeave={() => setHoveredLevelId(null)} // Reset hovered level ID on mouse leave
        >
          <div
            onClick={() => editingLevelId !== level.id && handleLevelClick(level)}
            className="flex items-center justify-between mb-2"
          >
            <span className="font-bold text-gray-700">
              {level.type}
            </span>
            <div className="text-sm text-gray-500 space-x-3 flex items-center">
              {editingLevelId === level.id ? (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    value={editingLevelName}
                    onChange={(e) => setEditingLevelName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSaveLevelName()}
                    onKeyDown={(e) => e.key === 'Escape' && handleCancelEditLevelName()}
                    autoFocus
                    className="border border-blue-500 px-2 py-1 text-sm rounded focus:outline-none"
                  />
                  <button
                    onClick={handleSaveLevelName}
                    className="text-green-600 hover:text-green-800 font-bold"
                    title="Save"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEditLevelName}
                    className="text-red-600 hover:text-red-800 font-bold"
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditLevelName(level);
                  }}
                  className="cursor-pointer hover:text-blue-600"
                  title="Click to edit"
                >
                  {level.level_name}
                </span>
              )}
              {hoveredLevelId === level.id && editingLevelId !== level.id && ( // Show delete icon only for hovered level
                <button
                  className="text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(level.id);
                  }}
                  aria-label="Delete Level"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSelectedLevelDetails = () => {
    if (!selectedLevel) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Select a level to view details</p>
        </div>
      );
    }

    switch (selectedLevel.type) {
      case 'Feedback':
        return <AssigneeFeedbackList />;
      case 'Assessment':
        return (
          <AssessmentCategoryCards
            key={selectedLevel?.id}
            selectedLevel={selectedLevel}
            allCategories={allCategories}
            onConfirm={openConfirmLevelModal}
            onCategoriesChange={handleCategoriesChange}
          />
        );
      case 'Lesson':
        return <LessonContent />;
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select a level to view details</p>
          </div>
        );
    }
  };


  const renderConfigurationModal = () => {
    if (!isConfigModalOpen || !selectedLevel) return null;

    const getModalContent = () => {
      switch (selectedLevel.type) {
        case 'Assessment':
          return (
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold text-gray-700">Category Name:</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  <option value="">-- Select a Category --</option>
                  {allCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 font-semibold text-gray-700">Number of Questions:</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(e.target.value)}
                  placeholder="Enter number of questions (1-100)"
                />
              </div>
            </div>
          );

        case 'Lesson':
          return (
            <div className="space-y-4">
              <div>
                <label className="block mb-2">Lesson Title:</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  placeholder="Enter lesson title"
                />
              </div>
              <div>
                <label className="block mb-2">Content:</label>
                <textarea
                  className="w-full p-2 border rounded-md h-32"
                  value={lessonContent}
                  onChange={(e) => setLessonContent(e.target.value)}
                  placeholder="Enter lesson content"
                ></textarea>
              </div>
            </div>
          );

        case 'Feedback':
          return (
            <div className="space-y-4">
              <div>
                <label className="block mb-2">Assignee Name:</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={assigneeName}
                  onChange={(e) => setAssigneeName(e.target.value)}
                  placeholder="Enter assignee name"
                />
              </div>
              <div>
                <label className="block mb-2">Email:</label>
                <input
                  type="email"
                  className="w-full p-2 border rounded-md"
                  value={assigneeEmail}
                  onChange={(e) => setAssigneeEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              Configure {selectedLevel.type}
            </h3>
            <button
              onClick={closeConfigModal}
              className="text-gray-500 hover:text-gray-700"
            >
              ✖
            </button>
          </div>
          {getModalContent()}
          <div className="flex justify-end mt-6 space-x-2">
            <button
              onClick={closeConfigModal}
              className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle save based on type
                closeConfigModal();
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!event) {
    return <div className="text-center">Event details not found.</div>;
  }
  const openConfigModal = () => setIsConfigModalOpen(true);
  const closeConfigModal = () => setIsConfigModalOpen(false);

  const openLessonModal = () => setIsLessonModalOpen(true);
  const closeLessonModal = () => {
    setIsLessonModalOpen(false);
    setLessonName('');
    setLessonCategory('Technical');
    setLessonDifficulty('Medium');
  };

  const openAssigneeModal = () => setIsAssigneeModalOpen(true);
  const closeAssigneeModal = () => {
    setIsAssigneeModalOpen(false);
    setAssigneeName('');
    setAssigneeType('Online');
  };

  const getConfigButton = () => {
    switch (selectedLevel.type) {
      case 'Assessment':
        return (
          <button
            onClick={openConfigModal}
            className="bg-gray-800 hover:bg-red-500 text-white px-6 py-2 rounded-md 
                     transition-colors duration-200 font-medium text-sm"
          >
            Configure Questions
          </button>
        );
      case 'Lesson':
        return (
          <button
            onClick={openLessonModal}
            className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 
                     transition-colors duration-200 font-medium text-sm"
          >
            Add Lesson
          </button>
        );
      case 'Feedback':
        return (
          <button
            onClick={openAssigneeModal}
            className="bg-purple-500 text-white px-6 py-2 rounded-md hover:bg-purple-600 
                     transition-colors duration-200 font-medium text-sm"
          >
            Add Assignee
          </button>
        );
      default:
        return null;
    }
  };

  const renderLessonModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Add Lesson</h3>
          <button onClick={closeLessonModal} className="text-gray-500 hover:text-gray-700">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-2">Lesson Name:</label>
            <input
              type="text"
              className="w-full p-2 border rounded-md"
              value={lessonName}
              onChange={(e) => setLessonName(e.target.value)}
              placeholder="Enter lesson name"
            />
          </div>
          <div>
            <label className="block mb-2">Category:</label>
            <select
              className="w-full p-2 border rounded-md"
              value={lessonCategory}
              onChange={(e) => setLessonCategory(e.target.value)}
            >
              <option value="Technical">Technical</option>
              <option value="Aptitude">Aptitude</option>
            </select>
          </div>
          <div>
            <label className="block mb-2">Difficulty:</label>
            <select
              className="w-full p-2 border rounded-md"
              value={lessonDifficulty}
              onChange={(e) => setLessonDifficulty(e.target.value)}
            >
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button onClick={closeLessonModal} className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-100">Cancel</button>
          <button onClick={handleLessonSubmit} className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600">Save</button>
        </div>
      </div>
    </div>
  );

  const renderAssigneeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Add Assignee</h3>
          <button onClick={closeAssigneeModal} className="text-gray-500 hover:text-gray-700">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-2">Assignee Name:</label>
            <input
              type="text"
              className="w-full p-2 border rounded-md"
              value={assigneeName}
              onChange={(e) => setAssigneeName(e.target.value)}
              placeholder="Enter assignee name"
            />
          </div>
          <div>
            <label className="block mb-2">Type:</label>
            <select
              className="w-full p-2 border rounded-md"
              value={assigneeType}
              onChange={(e) => setAssigneeType(e.target.value)}
            >
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button onClick={closeAssigneeModal} className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-100">Cancel</button>
          <button onClick={handleAssigneeSubmit} className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600">Save</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header headerTitle={"Event Details"} />
      <main className="flex-grow p-4 sm:p-6">
        <div className="bg-white shadow-md rounded-lg">
          {/* Stats Section */}
          <div className="flex justify-between items-center p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              <div className="flex items-center bg-gray-700 text-white text-xs font-bold py-2 px-3">
                <FontAwesomeIcon icon={faClock} className="mr-2" />
                <span>Duration: {event.duration} min</span>
              </div>
              <div className="flex items-center bg-gray-700 text-white text-xs font-bold py-2 px-3">
                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                <span>Candidates: {event.candidate_count}</span>
              </div>
              <div className="flex items-center bg-gray-700 text-white text-xs font-bold py-2 px-3">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                <span>Pass Percentage: {event.pass_percentage}%</span>
              </div>
              <div
                className={`text-white text-xs font-bold py-2 px-3 ${event.status === "upcoming" ? "bg-gray-500" : "bg-green-500"
                  }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                {event.status}
              </div>
            </div>
          </div>

          {/* Heading Section */}
          <div className="flex justify-between items-start p-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{event.name}</h1>
              <p className="text-sm sm:text-base text-gray-600">
                Institution: <span className="font-semibold">{event.institution}</span>
              </p>
              <p className="text-sm sm:text-base text-gray-600 mb-3">
                Date:{" "}
                <span className="font-semibold">
                  {new Date(event.event_date).toLocaleString()}
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/candidates/${event.id}`)}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm sm:text-base font-bold py-2 px-3 sm:px-4 rounded hover:opacity-80 transition flex items-center"
              >
                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                Candidates
              </button>
              <button
                onClick={() => navigate(`/consolidated/${event.id}`)}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white text-sm sm:text-base font-bold py-2 px-3 sm:px-4 rounded hover:opacity-80 transition flex items-center"
              >
                <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                Report
              </button>
            </div>
          </div>

          {/* Main Content Area with Aside */}
          <div className="flex flex-col lg:flex-row">
            {/* Aside with Levels */}
            <div className="lg:w-1/3 p-4 bg-gray-50 lg:border-r border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Event Levels</h2>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    className="bg-gray-800 hover:bg-red-500 rounded text-white px-4 py-2 flex items-center"
                  >
                    Add Level
                    <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                      <button
                        onClick={() => handleDropdownSelect("Lesson")}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      >
                        Lesson
                      </button>
                      <button
                        onClick={() => handleDropdownSelect("Assessment")}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      >
                        Assessment
                      </button>
                      <button
                        onClick={() => handleDropdownSelect("Feedback")}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      >
                        Feedback
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {renderLevels()}

              {/* Confirm Button */}
              {levels.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={openConfirmLevelModal}
                    disabled={!hasUnsavedChanges}
                    className={`px-6 py-3 rounded-lg font-semibold transition shadow-lg ${hasUnsavedChanges
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>

            {/* Selected Level Details Panel */}
            <div className="flex-grow">
              {renderSelectedLevelDetails()}
            </div>
          </div>
        </div>
      </main>
      {isLessonModalOpen && renderLessonModal()}
      {isConfigModalOpen && renderConfigurationModal()}
      {isAssigneeModalOpen && renderAssigneeModal()}

      {/* Category Configuration Confirmation Modal */}
      {isCategoryModalOpen && selectedLevelForConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                Configuration Details - {selectedLevelForConfig.level_name}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                Review the configured categories for this Assessment level
              </p>
            </div>

            <div className="p-6 max-h-96 overflow-y-auto">
              {selectedLevelForConfig.categories && selectedLevelForConfig.categories.length > 0 ? (
                <div className="space-y-3">
                  {selectedLevelForConfig.categories.map((category) => (
                    <div key={category.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-800">{category.category_name}</h3>
                          <p className="text-gray-600 text-sm mt-1">
                            Questions: <span className="font-semibold text-blue-600">{category.questions_count}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-600 text-sm">Duration: <span className="font-semibold">{category.duration} mins</span></p>
                          <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold text-white rounded ${category.difficulty === 'Easy' ? 'bg-green-500' :
                            category.difficulty === 'Medium' ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}>
                            {category.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <p>No categories configured yet</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeCategoryConfigModal}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Level Modal */}
      {isConfirmLevelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Confirm Level</h2>
              <p className="text-gray-600 mt-2">
                Do you want to confirm the Level?
              </p>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeConfirmLevelModal}
                disabled={isSubmittingLevel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No
              </button>
              <button
                onClick={handleConfirmLevel}
                disabled={isSubmittingLevel}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmittingLevel ? (
                  <>
                    <span className="inline-block animate-spin">⟳</span>
                    Saving...
                  </>
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EventDetails;
